import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const ROLES = [
  ["parent", "Parents"],
  ["vendor", "Vendors"],
  ["event_creator", "Event Creators"],
  ["marketplace_user", "Marketplace"],
];
const RANGES = [["30", "Last 30 days"], ["90", "Last 90 days"], ["0", "All time"]];

const Cell = ({ v, testid }) => (
  <td className="px-3 py-2.5 text-center whitespace-nowrap" data-testid={testid}>
    <span className="font-bold text-forest">{v?.signups || 0}</span>
    <span className="text-forest/40"> / </span>
    <span className="font-bold text-terracotta-dark">{v?.paying || 0}</span>
  </td>
);

const slug = (r) => `${r.campaign}-${r.source}-${r.medium}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function CampaignsTab() {
  const [days, setDays] = useState("30");
  const [rows, setRows] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/admin/campaign-report/export", { params: { days }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compass-kids-campaigns-${days === "0" ? "all-time" : `last-${days}-days`}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "Download failed"));
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    setRows(null);
    api.get("/admin/campaign-report", { params: { days } })
      .then((r) => setRows(r.data.rows))
      .catch((e) => toast.error(errMsg(e, "Could not load campaign report")));
  }, [days]);

  return (
    <div className="space-y-5" data-testid="campaigns-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-forest/60 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-sage-dark" />
          Each cell shows <b className="text-forest">sign-ups</b> / <b className="text-terracotta-dark">paying members</b>.
        </p>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="campaigns-range">
          {RANGES.map(([k, label]) => (
            <button key={k} onClick={() => setDays(k)} data-testid={`campaigns-range-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${days === k ? "bg-forest text-white" : "bg-white/70 text-forest/60 hover:text-forest border border-sage/20"}`}>
              {label}
            </button>
          ))}
          <Button onClick={download} disabled={downloading || !rows?.length} size="sm"
            className="ml-2 rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="campaigns-download-btn">
            <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Preparing..." : "Download CSV"}
          </Button>
        </div>
      </div>
      {!rows ? (
        <p className="text-sm text-forest/60" data-testid="campaigns-loading">Loading…</p>
      ) : !rows.length ? (
        <p className="text-sm text-forest/60" data-testid="campaigns-empty">No sign-ups in this period yet.</p>
      ) : (
        <div className="overflow-x-auto bg-white/70 border border-sage/20 rounded-2xl">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead>
              <tr className="text-left text-xs text-forest/60 border-b border-sage/20">
                <th className="px-4 py-3 font-bold">Campaign</th>
                <th className="px-3 py-3 font-bold">Source / medium</th>
                {ROLES.map(([k, label]) => <th key={k} className="px-3 py-3 font-bold text-center">{label}</th>)}
                <th className="px-3 py-3 font-bold text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={slug(r)} className="border-b border-sage/10 last:border-0 hover:bg-sage-50 transition-colors" data-testid={`campaign-row-${slug(r)}`}>
                  <td className="px-4 py-2.5 font-bold text-forest">{r.campaign}</td>
                  <td className="px-3 py-2.5 text-forest/60">{[r.source, r.medium].filter(Boolean).join(" / ") || "—"}</td>
                  {ROLES.map(([k]) => <Cell key={k} v={r.by_role[k]} testid={`campaign-${slug(r)}-${k}`} />)}
                  <Cell v={r} testid={`campaign-${slug(r)}-total`} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-forest/50">
        Paying members = accounts with an active or scheduled Paystack subscription. Campaign comes from the first UTM / ad link a visitor arrived on.
      </p>
    </div>
  );
}
